/**
 * Canonical schema.org node generators.
 *
 * This module centralizes all structured data about the clinic, doctors, and
 * services so every page can reference a single authoritative clinic node,
 * preventing dangling @id references in search engine parsing.
 *
 * CRITICAL CONSTRAINTS (researched decisions):
 * - Never emit AggregateRating or Review (Google makes pages ineligible for
 *   star feature when reviewed entity controls the reviews).
 * - Never add FAQPage markup (Google deprecated this for all site types
 *   2026-05-07; omitting is correct; guessing is not).
 * - Only include address/hours fields from clinic.ts that are verified.
 */

import type { Locale } from "@/content/types";
import { CLINIC, OPENING_HOURS } from "@/data/clinic";
import { SITE_URL } from "./seo";
import { OG_LOCALE } from "./locale";

/**
 * A JSON-LD node under construction.
 *
 * `unknown` rather than `any`: these objects are assembled and then serialised,
 * never read back, so nothing needs the escape hatch that `any` provides.
 */
type JsonLdNode = Record<string, unknown>;


/**
 * The canonical clinic entity node.
 *
 * Emit this on every page so @id references always resolve. Sources all values
 * from src/data/clinic.ts, the single source of truth for business facts.
 */
export function clinicNode(locale: Locale) {
  const name = locale === "ar" ? CLINIC.nameAr : CLINIC.name;
  const streetAddress = locale === "ar" ? CLINIC.address.streetAr : CLINIC.address.street;
  const addressLocality = locale === "ar" ? CLINIC.address.districtAr : CLINIC.address.district;

  const node: JsonLdNode = {
    "@context": "https://schema.org",
    "@type": ["Dentist", "MedicalBusiness", "LocalBusiness"],
    "@id": `${SITE_URL}/#clinic`,
    name,
    url: SITE_URL,
    telephone: CLINIC.phone.e164,
    address: {
      "@type": "PostalAddress",
      streetAddress,
      addressLocality,
      addressRegion: CLINIC.address.country,
      // Only include postalCode if verified; see CLINIC_VERIFICATION["postal-code"]
      ...(CLINIC.address.postalCode && { postalCode: CLINIC.address.postalCode }),
      addressCountry: CLINIC.address.country,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: CLINIC.geo.latitude,
      longitude: CLINIC.geo.longitude,
    },
    sameAs: [
      "https://www.facebook.com/Primero.Clinic/",
      "https://www.instagram.com/primero.clinic/",
    ],
  };

  // Opening hours: only emit if verified (status === "verified")
  if (OPENING_HOURS.status === "verified" && OPENING_HOURS.days.length > 0) {
    node.openingHoursSpecification = OPENING_HOURS.days
      .filter((day) => day.opens !== null && day.closes !== null)
      .map((day) => ({
        "@type": "OpeningHoursSpecification",
        dayOfWeek: day.dayOfWeek,
        opens: day.opens,
        closes: day.closes,
      }));
  }

  return node;
}

/**
 * The website/organization entity node (parent of clinic).
 *
 * Provides schema context for organization-level properties like logo
 * (not yet used; included for future expansion).
 */
export function organizationNode(locale: Locale) {
  const name = locale === "ar" ? CLINIC.nameAr : CLINIC.name;

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.svg`,
    sameAs: [
      "https://www.facebook.com/Primero.Clinic/",
      "https://www.instagram.com/primero.clinic/",
    ],
  };
}

/**
 * Breadcrumb trail node.
 *
 * Usage: `breadcrumbNode(locale, [{ name: "Services", url: "/en/services" }, { name: "Implants", url: "/en/services/implants" }])`
 */
export function breadcrumbNode(
  locale: Locale,
  trail: Array<{ name: string; url: string }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Service offering node for a dental service.
 *
 * Usage: `serviceNode(locale, { name: "Implants", description: "...", url: "/en/services/implants", medicalProcedure?: "DentalImplants" })`
 */
export function serviceNode(locale: Locale, opts: {
  name: string;
  description: string;
  url: string;
  medicalProcedure?: string;
}) {
  const node: JsonLdNode = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: opts.name,
    description: opts.description,
    url: opts.url,
    provider: { "@id": `${SITE_URL}/#clinic` },
  };

  if (opts.medicalProcedure) {
    node.procedureType = opts.medicalProcedure;
  }

  return node;
}
